import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TrackByFunction,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { map } from 'rxjs/operators';
import { ModalService } from '../../../../core/modal/modal.service';
import { UtilsService } from '../../../../core/services/utils.service';
import {
  ListModalComponent,
  ListModalOutput,
} from '../../../../shared/components/modals/list-modal/list-modal.component';
import { Selected } from '../../../../shared/selected.pipe';
import { RetailPlacesApiService } from '../../../../core/modules/retail-places/retail-places-api.service';
import { RetailPlaceService } from '../../../../core/services/retail-place.service';
import { RetailPlace } from '../../../../core/modules/retail-places/retail-paces.types';
import { Permission } from '../../../../core/modules/admin/admin.types';

type RetailPlaceWithSelectedState = RetailPlace & Selected;

@UntilDestroy()
@Component({
  selector: 'tax-user-retail-places',
  templateUrl: 'user-retail-places.component.html',
  styleUrls: ['../user.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class UserRetailPlacesComponent implements OnInit {
  @Input() set selectedRetailPlaces(selectedRetailPlaces: number[] | undefined) {
    if (!selectedRetailPlaces) {
      return;
    }

    this._selectedRetailPlaces = selectedRetailPlaces;
    this.retailPlaces = this.setStateSelected(this.retailPlaces, selectedRetailPlaces);
    this.cdr.detectChanges();
  }

  @Input() userPermissions: Permission[] = [];

  @Output() retailPlacesChanges = new EventEmitter<number[]>();
  @Output() toggleFullRetailPlaceAccess = new EventEmitter<boolean>();

  readonly trackById: TrackByFunction<any> = this.utilsService.trackById;

  retailPlaces: RetailPlaceWithSelectedState[] = [];

  private _selectedRetailPlaces: number[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private modalService: ModalService,
    private retailPlaceService: RetailPlaceService,
    private retailPlacesApiService: RetailPlacesApiService,
    private utilsService: UtilsService,
  ) {
  }

  ngOnInit(): void {
    this.setRetailPlaces();
  }

  selectRetailPlacesModal(): void {
    this.modalService.openOld<ListModalOutput<RetailPlaceWithSelectedState>>(
      ListModalComponent,
      {
        isFullAccess: this.retailPlaceService.isFullRetailPlaceAccess(this.userPermissions),
        type: 'retailPlaces',
        items: this.retailPlaces,
        propertiesMap: {
          description: 'address',
        },
      },
      {
        width: '740px',
      },
    )
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        this.retailPlaces = result.items;
        this.cdr.detectChanges();

        this.toggleFullRetailPlaceAccess.emit(result.isFullAccess);

        const selectedRetailPlacesIds = result.items
          .filter(retailPlace => retailPlace.selected)
          .map(retailPlace => retailPlace.id);
        this.retailPlacesChanges.emit(selectedRetailPlacesIds);
      });
  }

  private setRetailPlaces(): void {
    this.retailPlacesApiService.fetchRetailPlaces()
      .pipe(
        map((retailPlaces) => {
          const isFullRetailPlaceAccess = this.retailPlaceService.isFullRetailPlaceAccess(this.userPermissions);
          if (isFullRetailPlaceAccess) {
            retailPlaces.forEach((retailPlace) => {
              (retailPlace as RetailPlaceWithSelectedState).selected = true;
            });
            return retailPlaces as RetailPlaceWithSelectedState[];
          }

          return this.setStateSelected(retailPlaces, this._selectedRetailPlaces);
        }),
        untilDestroyed(this),
      )
      .subscribe((retailPlaces) => {
        this.retailPlaces = retailPlaces;
        this.cdr.detectChanges();
      });
  }

  private setStateSelected(retailPlaces: RetailPlaceWithSelectedState[], selectedRetailPlaces: number[]): RetailPlaceWithSelectedState[] {
    selectedRetailPlaces.forEach((selectedRetailPlaceId) => {
      const selectedRetailPlace = retailPlaces.find(retailPlace => retailPlace.id === selectedRetailPlaceId);
      if (selectedRetailPlace) {
        selectedRetailPlace.selected = true;
      }
    });

    return retailPlaces;
  }
}
